import { useTranslation } from 'react-i18next';
import { ExtraWorkOrder } from '../../../List/models';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faBoxesStacked,
  faMapLocationDot,
  faNoteSticky,
  faPlay,
  faCheck,
} from '@fortawesome/pro-light-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

interface OrderHeaderProps {
  order: ExtraWorkOrder;
}

/**
 * Header con información principal de la orden
 */
export const OrderHeader = ({ order }: OrderHeaderProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');

  // Consultar ubicación si solo tenemos location_id
  const needsLocationFetch = Boolean(!order.location?.code && !order.location?.name && order.location_id);

  const { data: locationData } = useQuery({
    queryKey: ['location', order.location_id],
    queryFn: async () => {
      if (!order.location_id) return null;
      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.get<any>('/locations', {
        ids: String(order.location_id),
        per_page: 1,
      });
      return response.data?.[0] || null;
    },
    enabled: needsLocationFetch,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  // Consultar mercancía si solo tenemos merchandise_id
  const needsMerchandiseFetch = Boolean(!order.merchandise?.name && !order.merchandise?.ref && order.merchandise_id);

  const { data: merchandiseData } = useQuery({
    queryKey: ['merchandise', order.merchandise_id],
    queryFn: async () => {
      if (!order.merchandise_id) return null;
      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.get<any>(`/merchandise/${order.merchandise_id}`);
      return response || null;
    },
    enabled: needsMerchandiseFetch,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  // Determinar el nombre de la ubicación a mostrar
  const locationName =
    order.location?.code ||
    order.location?.name ||
    locationData?.code ||
    locationData?.name ||
    (order.location_id ? String(order.location_id) : '-');

  // Determinar el nombre de la mercancía a mostrar
  const merchandiseName =
    order.merchandise?.name ||
    order.merchandise?.ref ||
    merchandiseData?.name ||
    merchandiseData?.ref ||
    (order.merchandise_id ? String(order.merchandise_id) : '-');

  // Determinar si mostrar "Iniciada" en la segunda fila (estados distintos de DONE)
  const showStartedInSecondRow = Boolean(order.started_at) && order.status !== 'DONE';

  // En DONE, "Iniciada" va en el hueco de la primera fila
  const showStartedInFirstRow = Boolean(order.started_at) && order.status === 'DONE';

  // Contar bloques que se mostrarán
  let blockCount = 2; // Mercancía y Ubicación origen siempre están
  // Fecha límite solo se muestra si NO está completada
  if (order.deadline_at && order.status !== 'DONE') blockCount++;
  if (showStartedInFirstRow) blockCount++;
  if (order.notes) blockCount++;
  if (showStartedInSecondRow) blockCount++;
  if (order.finished_at && !showStartedInSecondRow) blockCount++;

  // Notas ocupa 1 columna en mobile (50%), 2 o 3 columnas en desktop según conteo
  const notesSpan = blockCount === 4 ? 'col-span-1 lg:col-span-3' : 'col-span-1 lg:col-span-2';

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      {/* Primera fila: Fecha límite (si existe y NO está completada), Mercancía, Ubicación origen */}
      {/* Fecha límite - Primer item - Solo mostrar si NO está completada */}
      {order.deadline_at && order.status !== 'DONE' && (
        <SectionBox className="!space-y-0 !p-3">
          <div className="flex items-center gap-2 h-full">
            <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 text-info" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-medium text-complementary-text mb-0.5">
                {t('detail.header.deadline', 'Fecha límite')}
              </h3>
              <p className="text-sm font-bold text-title truncate">
                {format(new Date(order.deadline_at), 'dd/MM/yyyy')}
              </p>
            </div>
          </div>
        </SectionBox>
      )}

      {/* Mercancía */}
      <SectionBox className="!space-y-0 !p-3">
        <div className="flex items-center gap-2 h-full">
          <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faBoxesStacked} className="w-3 h-3 text-icons" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-medium text-complementary-text mb-0.5">
              {t('detail.header.merchandise', 'Mercancía')}
            </h3>
            <p className="text-sm font-bold text-title truncate">{merchandiseName}</p>
          </div>
        </div>
      </SectionBox>

      {/* Ubicación origen */}
      <SectionBox className="!space-y-0 !p-3">
        <div className="flex items-center gap-2 h-full">
          <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faMapLocationDot} className="w-3 h-3 text-info" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-medium text-complementary-text mb-0.5">
              {t('detail.header.location', 'Ubicación origen')}
            </h3>
            <p className="text-sm font-bold text-title truncate">{locationName}</p>
          </div>
        </div>
      </SectionBox>

      {/* Iniciada - Mostrar en primera fila cuando estado es DONE */}
      {showStartedInFirstRow && (
        <SectionBox className="!space-y-0 !p-3">
          <div className="flex items-center gap-2 h-full">
            <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faPlay} className="w-3 h-3 text-info" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-medium text-complementary-text mb-0.5">
                {t('detail.header.started_at', 'Iniciada')}
              </h3>
              <p className="text-sm font-bold text-title truncate">
                {order.started_at ? format(new Date(order.started_at), 'dd/MM/yyyy HH:mm') : '-'}
              </p>
            </div>
          </div>
        </SectionBox>
      )}

      {/* Segunda fila: Notas (2 o 3 columnas según cantidad de bloques) y Iniciada (si estado es PENDING o IN_PROGRESS) o Finalizada */}
      {/* Notas */}
      {order.notes && (
        <SectionBox className={`!space-y-0 !p-3 ${notesSpan}`}>
          <div className="flex items-center gap-2 h-full">
            <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faNoteSticky} className="w-3 h-3 text-icons" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-medium text-complementary-text mb-0.5">
                {t('detail.header.notes', 'Notas')}
              </h3>
              <p className="text-xs text-text line-clamp-2">{order.notes}</p>
            </div>
          </div>
        </SectionBox>
      )}

      {/* Iniciada - Mostrar en segunda fila si estado NO es DONE */}
      {showStartedInSecondRow && (
        <SectionBox className="!space-y-0 !p-3">
          <div className="flex items-center gap-2 h-full">
            <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faPlay} className="w-3 h-3 text-info" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-medium text-complementary-text mb-0.5">
                {t('detail.header.started_at', 'Iniciada')}
              </h3>
              <p className="text-sm font-bold text-title truncate">
                {order.started_at ? format(new Date(order.started_at), 'dd/MM/yyyy HH:mm') : '-'}
              </p>
            </div>
          </div>
        </SectionBox>
      )}

      {/* Finalizada - Mostrar cuando estado es DONE */}
      {order.finished_at && order.status === 'DONE' && (
        <SectionBox className="!space-y-0 !p-3">
          <div className="flex items-center gap-2 h-full">
            <div className="w-6 h-6 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faCheck} className="w-3 h-3 text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-medium text-complementary-text mb-0.5">
                {t('detail.header.finished_at', 'Finalizada')}
              </h3>
              <p className="text-sm font-bold text-title truncate">
                {format(new Date(order.finished_at), 'dd/MM/yyyy HH:mm')}
              </p>
            </div>
          </div>
        </SectionBox>
      )}
    </div>
  );
};
